import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileBarChart } from "lucide-react";
import { apiGetAnalyticsPivotDataset } from "@/lib/api";
import {
  AGG_FNS,
  getDimensionFields,
  getMeasureFields,
  getFieldByKey,
  type AggFn,
  type FlatEmployeeRow,
} from "@/lib/analytics/fieldCatalog";
import {
  computePivot,
  flattenPivotForExport,
  cellKey,
  TOTAL_COL,
  HEADCOUNT_KEY,
  type PivotValueSpec,
} from "@/lib/analytics/pivot";

/**
 * Mobile-friendly simplified pivot builder: one Row field, optional one
 * Value+agg, optional one Filter, table output + CSV export. Reuses the same
 * shared engine as the desktop builder.
 */
export function MobilePivotReportBuilder() {
  const { t } = useTranslation();

  const { data: dataset = [], isLoading } = useQuery({
    queryKey: ["analytics-pivot-dataset"],
    queryFn: apiGetAnalyticsPivotDataset,
    staleTime: 5 * 60 * 1000,
  });

  const dims = getDimensionFields();
  const measures = getMeasureFields();

  const [rowField, setRowField] = useState<keyof FlatEmployeeRow>("department");
  const [valueField, setValueField] = useState<keyof FlatEmployeeRow | "">("");
  const [agg, setAgg] = useState<AggFn>("sum");
  const [filterField, setFilterField] = useState<keyof FlatEmployeeRow | "">("");
  const [filterValue, setFilterValue] = useState("");

  const labelFor = (key: keyof FlatEmployeeRow): string => {
    const f = getFieldByKey(key as string);
    return f ? t(f.labelKey) : String(key);
  };

  const values: PivotValueSpec[] = valueField ? [{ field: valueField, agg }] : [];

  const pivot = useMemo(
    () =>
      computePivot(dataset, {
        mode: "pivot",
        rowFields: [rowField],
        values,
        filters: filterField && filterValue ? [{ field: filterField, op: "eq", value: filterValue }] : [],
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataset, rowField, valueField, agg, filterField, filterValue]
  );

  const valueSpec = pivot.valueSpecs[0];
  const ck = valueSpec ? cellKey(valueSpec) : "";
  const headerLabel =
    valueSpec && valueSpec.field === (HEADCOUNT_KEY as keyof FlatEmployeeRow)
      ? t("analytics.pivot.headcount")
      : valueSpec
        ? `${t(`analytics.pivot.agg.${valueSpec.agg}`)}(${labelFor(valueSpec.field)})`
        : t("analytics.pivot.headcount");

  function exportCsv() {
    const rows = flattenPivotForExport(pivot, labelFor, {
      headcountLabel: t("analytics.pivot.headcount"),
      totalLabel: t("analytics.pivot.grandTotal"),
    });
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hr-pivot-report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const selectCls = "w-full h-10 rounded-xl border border-border bg-card px-3 text-sm";

  return (
    <div className="bg-card border border-border/60 rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <FileBarChart className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground">{t("analytics.pivot.title")}</h2>
      </div>

      {/* Row field */}
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">{t("analytics.pivot.groupBy")}</label>
        <select className={selectCls} value={rowField as string} onChange={(e) => setRowField(e.target.value as keyof FlatEmployeeRow)}>
          {dims.map((f) => (
            <option key={f.key} value={f.key as string}>
              {t(f.labelKey)}
            </option>
          ))}
        </select>
      </div>

      {/* Value + agg */}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("analytics.pivot.value")}</label>
          <select className={selectCls} value={valueField as string} onChange={(e) => setValueField(e.target.value as keyof FlatEmployeeRow | "")}>
            <option value="">{t("analytics.pivot.headcount")}</option>
            {measures.map((f) => (
              <option key={f.key} value={f.key as string}>
                {t(f.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("analytics.pivot.aggregation")}</label>
          <select className={selectCls} value={agg} onChange={(e) => setAgg(e.target.value as AggFn)} disabled={!valueField}>
            {AGG_FNS.map((a) => (
              <option key={a} value={a}>
                {t(`analytics.pivot.agg.${a}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Optional filter */}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("analytics.pivot.filterBy")}</label>
          <select className={selectCls} value={filterField as string} onChange={(e) => setFilterField(e.target.value as keyof FlatEmployeeRow | "")}>
            <option value="">{t("analytics.pivot.none")}</option>
            {dims.map((f) => (
              <option key={f.key} value={f.key as string}>
                {t(f.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("analytics.pivot.filterValue")}</label>
          <input
            className={selectCls}
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            placeholder={t("analytics.pivot.filterValue")}
            disabled={!filterField}
          />
        </div>
      </div>

      {/* Result */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-6">{t("analytics.loading")}</p>
      ) : pivot.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">{t("analytics.pivot.noData")}</p>
      ) : (
        <>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-medium">{labelFor(rowField)}</th>
                  <th className="text-right py-2 px-2 font-medium">{headerLabel}</th>
                </tr>
              </thead>
              <tbody>
                {pivot.rows.map((row, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 px-2">{row.rowKey[0]}</td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      {fmt(ck ? row.cells[TOTAL_COL]?.[ck] : null)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 font-semibold">
                  <td className="py-2 px-2">{t("analytics.pivot.grandTotal")}</td>
                  <td className="py-2 px-2 text-right tabular-nums">
                    {fmt(ck ? pivot.grandTotals[TOTAL_COL]?.[ck] : null)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="w-full h-10 rounded-xl bg-primary text-primary-foreground text-sm font-medium"
          >
            {t("analytics.pivot.exportCsv")}
          </button>
        </>
      )}
    </div>
  );
}

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return "–";
  return (Math.round(n * 100) / 100).toLocaleString();
}
